import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Button, Dialog, DialogTitle, DialogContent, IconButton, Chip, CircularProgress, Alert, Tooltip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { bloomberg } from '../theme/bloombergTheme';
import { getThemes, buildThemeModel, ThemeSummary } from '../services/api';

const PROVIDER_COLOR: Record<string, string> = { iShares: '#111827', 'First Trust': '#1d4ed8', VanEck: '#b45309' };

/**
 * The systematic theme models: each theme pools the thematic ETFs of iShares,
 * First Trust and VanEck that cover it, and its base model holds the top 20
 * names they hold most widely, equal-weighted (backend theme_library.py).
 */
export function ThemesDialog({ open, onClose, onOpenModel, onChanged }: {
  open: boolean;
  onClose: () => void;
  onOpenModel: (id: number) => void;
  onChanged: () => void;
}) {
  const [themes, setThemes] = useState<ThemeSummary[]>([]);
  const [asOf, setAsOf] = useState('');
  const [building, setBuilding] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    try {
      const r = await getThemes();
      setThemes(r.themes);
      setAsOf(r.snapshot_date);
    } catch {
      setError('Could not load the theme list.');
    }
  };
  useEffect(() => { if (open) load(); }, [open]);

  const build = async (key: string) => {
    setBuilding(key); setError(null);
    try {
      await buildThemeModel(key);
      await load();
      onChanged();
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Build failed.');
    } finally {
      setBuilding(null);
    }
  };

  // One at a time: each build reads our database first and only asks Yahoo
  // about names we don't hold yet.
  const buildAllMissing = async () => {
    setError(null);
    const todo = themes.flatMap((t) => [t, ...(t.children || [])]).filter((t) => !t.model_id);
    for (const t of todo) {
      setBuilding(t.key);
      try {
        await buildThemeModel(t.key);
      } catch (e: any) {
        setError(`${t.label}: ${e?.response?.data?.detail || 'build failed'}. Stopped here.`);
        break;
      }
    }
    setBuilding(null);
    await load();
    onChanged();
  };
  const missing = themes.flatMap((t) => [t, ...(t.children || [])]).filter((t) => !t.model_id).length;

  const row = (t: ThemeSummary, child: boolean) => (
    <Box key={t.key} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: child ? 0.7 : 1.1, pl: child ? 3 : 0,
      borderBottom: `1px solid ${bloomberg.border.primary}` }}>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ fontSize: child ? '0.84rem' : '0.9rem', fontWeight: child ? 500 : 700, color: bloomberg.text.primary }}>
          {child ? '↳ ' : ''}{t.label}
        </Typography>
        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.4 }}>
          {t.etfs.map((e) => (
            <Tooltip key={e.ticker} title={`${e.name || e.ticker} · ${e.provider || ''}${e.holdings ? ` · ${e.holdings} holdings` : ''}`}>
              <Chip size="small" label={e.ticker} variant="outlined"
                sx={{ height: 19, fontSize: '0.68rem', fontFamily: 'monospace', borderColor: PROVIDER_COLOR[e.provider || ''] || bloomberg.border.primary, color: bloomberg.text.secondary }} />
            </Tooltip>
          ))}
        </Box>
      </Box>
      {t.model_id && (
        <Button size="small" onClick={() => { onOpenModel(t.model_id as number); onClose(); }} sx={{ textTransform: 'none' }}>
          Open
        </Button>
      )}
      <Button size="small" variant={t.model_id ? 'text' : 'contained'} disabled={!!building} onClick={() => build(t.key)}
        sx={{ textTransform: 'none', minWidth: 84 }}>
        {building === t.key ? <CircularProgress size={14} /> : t.model_id ? 'Rebuild' : 'Build'}
      </Button>
    </Box>
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth
      PaperProps={{ sx: { bgcolor: bloomberg.bg.secondary, border: `1px solid ${bloomberg.border.primary}` } }}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', pb: 1 }}>
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ fontWeight: 700, fontSize: '1rem', color: bloomberg.text.primary }}>Theme models</Typography>
          <Typography sx={{ fontSize: '0.78rem', color: bloomberg.text.muted }}>
            Each theme pools the thematic ETFs of iShares, First Trust and VanEck that cover it. The base model holds the
            20 names they hold most widely (above $1bn), equal-weighted. Sub-themes (↳) do the same for a narrower set
            of ETFs; one fed by a single ETF is that fund's top names. ETF holdings as of {asOf || '…'}.
          </Typography>
        </Box>
        <IconButton size="small" onClick={onClose}><CloseIcon fontSize="small" /></IconButton>
      </DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 1.5 }}>{error}</Alert>}
        {themes.length === 0 && !error && <CircularProgress size={20} />}
        {missing > 0 && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <Button size="small" variant="contained" disabled={!!building} onClick={buildAllMissing} sx={{ textTransform: 'none' }}>
              Build all missing ({missing})
            </Button>
            {building && <Typography sx={{ fontSize: '0.76rem', color: bloomberg.text.muted }}>Building one at a time…</Typography>}
          </Box>
        )}
        {themes.map((t) => (
          <React.Fragment key={t.key}>
            {row(t, false)}
            {(t.children || []).map((c) => row(c, true))}
          </React.Fragment>
        ))}
        <Typography sx={{ fontSize: '0.72rem', color: bloomberg.text.muted, mt: 1.5 }}>
          ETF colours: <span style={{ color: PROVIDER_COLOR.iShares }}>■</span> iShares ·{' '}
          <span style={{ color: PROVIDER_COLOR['First Trust'] }}>■</span> First Trust ·{' '}
          <span style={{ color: PROVIDER_COLOR.VanEck }}>■</span> VanEck. Rebuilding re-ranks from the same ETF snapshot and
          today's market caps; the change is recorded in the rebalance history.
        </Typography>
      </DialogContent>
    </Dialog>
  );
}

/** "How this was built" for a theme base model, from its build_spec. */
export function ThemeBuildCard({ spec }: { spec: Record<string, any> }) {
  const [showAll, setShowAll] = useState(false);
  const rows: any[] = spec.holdings || [];
  const skipped: any[] = spec.skipped || [];
  const shown = showAll ? rows : rows.slice(0, 8);
  const pct = (v: number) => `${v.toFixed(1)}%`;
  return (
    <Box sx={{ bgcolor: bloomberg.bg.secondary, border: `1px solid ${bloomberg.border.primary}`, borderRadius: 1, p: 1.5, mb: 2 }}>
      <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, color: bloomberg.text.muted, mb: 0.5 }}>
        How this model was built
      </Typography>
      <Typography sx={{ fontSize: '0.82rem', color: bloomberg.text.secondary, mb: 1 }}>
        {spec.method} Source ETFs: {(spec.etfs || []).join(', ')} (holdings as of {spec.snapshot_date}).
        {' '}Floor ${((spec.min_mcap_usd || 0) / 1e9).toFixed(1)}bn · built {String(spec.built_at || '').slice(0, 10)}.
      </Typography>
      <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.78rem', '& td, & th': { py: 0.35, pr: 1.5, textAlign: 'left', borderBottom: `1px solid ${bloomberg.border.primary}` }, '& th': { color: bloomberg.text.muted, fontWeight: 600 } }}>
        <thead><tr><th>Name</th><th>Held by</th><th>Avg weight in theme ETFs</th><th>Market cap</th></tr></thead>
        <tbody>
          {shown.map((h) => (
            <tr key={h.symbol}>
              <td style={{ color: bloomberg.text.primary }}><b>{h.symbol}</b> <span style={{ color: bloomberg.text.muted }}>{h.name}</span></td>
              <td title={Object.entries(h.held_by || {}).map(([e, w]) => `${e} ${pct(Number(w))}`).join(', ')}>{h.n_held} of {h.n_etfs} ETFs</td>
              <td>{pct(Number(h.avg_weight || 0))}</td>
              <td title={h.mcap_source ? `${h.mcap_source}${h.mcap_as_of ? `, as of ${h.mcap_as_of}` : ''}` : undefined}>{h.mcap_usd ? `$${(h.mcap_usd / 1e9).toFixed(1)}bn` : '—'}</td>
            </tr>
          ))}
        </tbody>
      </Box>
      {rows.length > 8 && (
        <Button size="small" onClick={() => setShowAll((v) => !v)} sx={{ textTransform: 'none', mt: 0.5 }}>
          {showAll ? 'Show fewer' : `Show all ${rows.length}`}
        </Button>
      )}
      {skipped.length > 0 && (
        <Typography sx={{ fontSize: '0.74rem', color: bloomberg.text.muted, mt: 1 }}>
          Left out: {skipped.map((s) => `${s.name} (${s.reason})`).join('; ')}.
        </Typography>
      )}
    </Box>
  );
}
